import * as ipaddr from 'ipaddr.js';

const INTERNET_NUMBER_RESOURCE_TYPE = {
  INVALID: 0,
  AS_NUMBER: 1,
  IPV4_ADDRESS: 2,
  IPV6_ADDRESS: 3,
  IPV4_PREFIX: 4,
  IPV6_PREFIX: 5,
};

// If resource list is empty, return 0
// If resource list contains only prefix, return 1
// If resource list contains only AS, return 2
// IF resource list contains both prefix and AS, return 3
const RESOURCE_LIST_STATUS = {
  EMPTY: 0,
  PREFIX_ONLY: 1,
  AS_ONLY: 2,
  PREFIX_AND_AS: 3,
};

const normalizeInternetNumber = (resource) => {
  const internetNumber = resource.replace(/^as/i, '');
  const type = validateInternetNumberType(internetNumber);

  switch (type) {
    case INTERNET_NUMBER_RESOURCE_TYPE.AS_NUMBER:
      return `AS${internetNumber}`;

    case INTERNET_NUMBER_RESOURCE_TYPE.IPV4_ADDRESS:
      return internetNumber;

    case INTERNET_NUMBER_RESOURCE_TYPE.IPV6_ADDRESS:
      return internetNumber;

    case INTERNET_NUMBER_RESOURCE_TYPE.IPV4_PREFIX:
      return normalizeIPv4Prefix(internetNumber);

    case INTERNET_NUMBER_RESOURCE_TYPE.IPV6_PREFIX: {
      const address = ipaddr.IPv6.networkAddressFromCIDR(internetNumber);
      const length = internetNumber.split('/')[1];
      return `${address}/${length}`;
    }

    default:
      return resource;
  }
};

const validateInternetNumberType = (internetNumber) => {
  const isASNumber = checkIsASNumber(internetNumber);
  if (isASNumber) return INTERNET_NUMBER_RESOURCE_TYPE.AS_NUMBER;

  // if the input of ipaddre.IPv4.isValid() is a number, e.g. 0, 123, it returns true, which is not proper here, so added IPv4Regex to make sure the result of IPv4 validation is correct.
  const IPv4Regex = /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)(\.(?!$)|$)){4}$/;
  const isValidIPv4 =
    ipaddr.IPv4.isValid(internetNumber) && internetNumber.match(IPv4Regex);
  if (isValidIPv4) return INTERNET_NUMBER_RESOURCE_TYPE.IPV4_ADDRESS;

  const isValidIPv6 = ipaddr.IPv6.isValid(internetNumber);
  if (isValidIPv6) return INTERNET_NUMBER_RESOURCE_TYPE.IPV6_ADDRESS;

  const isValidIPv4Prefix = checkIsValidIPv4Prefix(internetNumber);
  if (isValidIPv4Prefix) return INTERNET_NUMBER_RESOURCE_TYPE.IPV4_PREFIX;

  const isValidIPv6Prefix = checkIsValidIPv6Prefix(internetNumber);
  if (isValidIPv6Prefix) return INTERNET_NUMBER_RESOURCE_TYPE.IPV6_PREFIX;

  return INTERNET_NUMBER_RESOURCE_TYPE.INVALID;
};

const checkIsASNumber = (value) => {
  const autnumRegex = new RegExp('^(as)?([0-9]+)$', 'i');

  if (!value.match(autnumRegex)) return false;

  const ASNumber = value.match(autnumRegex)[2];

  // ASNumber needs to be changed to number type, otherwise it is string type
  const isInAsNumberRange = validateIsInASNumberRange(+ASNumber);

  if (!isInAsNumberRange) return false;
  return true;
};

const validateIsInASNumberRange = (number) => {
  if (number > 0 && number < 4294967295 && number !== 65535) return true;
  return false;
};

const checkIsValidIPv4Prefix = (value) => {
  try {
    const result = ipaddr.IPv4.networkAddressFromCIDR(value);
    if (result) return true;
    return false;
  } catch (error) {
    return false;
  }
};

const checkIsValidIPv6Prefix = (value) => {
  try {
    const result = ipaddr.IPv6.networkAddressFromCIDR(value);
    if (result) return true;
    return false;
  } catch (error) {
    return false;
  }
};

// Function normalizeIPv4Prefix is to handle IPv4 prefix in an incomplete format, e.g. 8.8/16 or 8.8.8/24
const normalizeIPv4Prefix = (value) => {
  const base = value.split('/')[0];
  const length = value.split('/')[1];
  const IPv4Regex = /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)(\.(?!$)|$)){4}$/;

  const isNormalizedIPv4Prefix = base.match(IPv4Regex);

  if (isNormalizedIPv4Prefix) {
    const address = ipaddr.IPv4.networkAddressFromCIDR(`${base}/${length}`);
    return `${address}/${length}`;
  } else {
    // basePartsCounter will be 4 if the base is a normalized one, e.g. 128.128.128.128.split(".") = [128,128,128,128]
    const basePartsCounter = base.split('.').length;
    const missingParts = 4 - basePartsCounter;

    var normalizedBase = base;
    for (var i = 0; i < missingParts; i++) {
      // e.g. missingParts for the base of 8.8/16 will be 2, so concat ".0" twice to set it as 8.8.0.0
      normalizedBase = normalizedBase.concat('.0');
    }

    const address = ipaddr.IPv4.networkAddressFromCIDR(
      `${normalizedBase}/${length}`
    );
    return `${address}/${length}`;
  }
};

const checkIsSameResourceType = (currentResourceType, targetResourceType) => {
  if (
    currentResourceType === INTERNET_NUMBER_RESOURCE_TYPE.AS_NUMBER &&
    targetResourceType === INTERNET_NUMBER_RESOURCE_TYPE.AS_NUMBER
  )
    return true;

  if (
    (currentResourceType === INTERNET_NUMBER_RESOURCE_TYPE.IPV4_ADDRESS ||
      currentResourceType === INTERNET_NUMBER_RESOURCE_TYPE.IPV6_ADDRESS) &&
    (targetResourceType === INTERNET_NUMBER_RESOURCE_TYPE.IPV4_ADDRESS ||
      targetResourceType === INTERNET_NUMBER_RESOURCE_TYPE.IPV6_ADDRESS)
  )
    return true;

  if (
    (currentResourceType === INTERNET_NUMBER_RESOURCE_TYPE.IPV4_PREFIX ||
      currentResourceType === INTERNET_NUMBER_RESOURCE_TYPE.IPV6_PREFIX) &&
    (targetResourceType === INTERNET_NUMBER_RESOURCE_TYPE.IPV4_PREFIX ||
      targetResourceType === INTERNET_NUMBER_RESOURCE_TYPE.IPV6_PREFIX)
  )
    return true;

  return false;
};

export {
  INTERNET_NUMBER_RESOURCE_TYPE,
  RESOURCE_LIST_STATUS,
  validateInternetNumberType,
  normalizeIPv4Prefix,
  normalizeInternetNumber,
  checkIsSameResourceType,
};
